import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Buildings, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT, LangToggle } from "../i18n";

export default function JoinInvitePage() {
  const { user, loading } = useAuth();
  const { t } = useT();
  const token = new URLSearchParams(window.location.search).get("token");
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      return;
    }
    api.get(`/invitations/token/${token}`).then((r) => setInfo(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invitations/token/${token}/accept`);
      localStorage.removeItem("pending_invite");
      toast.success(`${t("toast_joined")} ${data.company_name}`);
      window.location.href = "/";
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const signinFirst = () => {
    localStorage.setItem("pending_invite", token);
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen bg-pro flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-900">
            <Receipt size={22} weight="bold" />
            <span className="font-heading font-bold tracking-tight">Pellku</span>
          </div>
          <LangToggle />
        </div>

        {invalid ? (
          <div className="space-y-4" data-testid="join-invalid">
            <p className="font-heading text-xl font-bold text-slate-900">{t("join_title")}</p>
            <p className="text-sm text-slate-500">{t("join_invalid")}</p>
            <Button data-testid="join-go-home-btn" onClick={() => (window.location.href = "/")} variant="outline" className="rounded-none w-full h-11">{t("goHome")}</Button>
          </div>
        ) : !info || loading ? (
          <p className="font-mono text-sm text-slate-400">{t("loading")}</p>
        ) : (
          <div className="space-y-5" data-testid="join-card">
            <div className="flex items-center gap-3 border border-slate-200 bg-slate-50 p-4">
              <Buildings size={28} className="text-slate-900 shrink-0" />
              <div className="min-w-0">
                <p className="font-heading font-bold text-slate-900 truncate" data-testid="join-company-name">{info.company_name}</p>
                <p className="text-xs text-slate-500">
                  {info.invited_by_name} {t("join_invited_by")} <b>{info.company_name}</b> {t("join_as")} <b>{t(`role_${info.role}`)}</b>
                </p>
              </div>
            </div>
            {user ? (
              <Button data-testid="join-accept-btn" onClick={accept} disabled={busy} className="rounded-none w-full h-11 bg-slate-900 hover:bg-slate-700">
                {t("join_accept")} <ArrowRight size={16} className="ml-2" />
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">{t("join_signin_first")}</p>
                <Button data-testid="join-signin-btn" onClick={signinFirst} className="rounded-none w-full h-11 bg-slate-900 hover:bg-slate-700">
                  {t("join_signin_cta")} <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
